import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];
const STROKES = ["M42 10 L30 90", "M70 10 L58 90", "M16 36 L86 36", "M12 64 L82 64"];
const WORDS = ["Brand", "Creative", "Digital", "PR", "Experiences"];
const COLUMNS = 5;
const HOLD = 2000;

export default function Preloader({ onReveal }) {
  const [phase, setPhase] = useState("draw");
  const [count, setCount] = useState(0);
  const [wordIdx, setWordIdx] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add("preloading");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const hold = reduced ? 300 : HOLD;

    if (!reduced) {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / (HOLD - 350), 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setCount(Math.round(eased * 100));
        if (p < 1) raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
    } else {
      setCount(100);
    }

    const wordTimer = setInterval(() => setWordIdx((i) => (i + 1) % WORDS.length), 420);

    const t = setTimeout(() => {
      setPhase("exit");
      onReveal();
      document.documentElement.classList.remove("preloading");
    }, hold);
    const t2 = setTimeout(() => setPhase("done"), hold + 1400);

    return () => {
      clearTimeout(t);
      clearTimeout(t2);
      clearInterval(wordTimer);
      if (raf.current) cancelAnimationFrame(raf.current);
      document.documentElement.classList.remove("preloading");
    };
  }, [onReveal]);

  if (phase === "done") return null;
  const complete = count >= 100;
  const exiting = phase === "exit";

  return (
    <div
      data-testid="preloader"
      className="pointer-events-none fixed inset-0 z-[999] overflow-hidden"
      aria-hidden="true"
    >
      {/* Staggered ink curtain */}
      <div className="absolute inset-0 flex">
        {Array.from({ length: COLUMNS }).map((_, i) => (
          <motion.div
            key={i}
            className="h-full flex-1 bg-ink"
            style={{ marginLeft: i === 0 ? 0 : -1 }}
            initial={{ y: 0 }}
            animate={exiting ? { y: "-100%" } : { y: 0 }}
            transition={{ duration: 0.85, delay: i * 0.065, ease: EASE }}
          />
        ))}
      </div>

      {/* Content layer */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 1 }}
        animate={exiting ? { opacity: 0, y: -60 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: EASE }}
      >
        {/* Faint grid lines */}
        <div className="absolute inset-0 flex opacity-[0.06]">
          {Array.from({ length: COLUMNS - 1 }).map((_, i) => (
            <div key={i} className="h-full flex-1 border-r border-bone" />
          ))}
          <div className="h-full flex-1" />
        </div>

        {/* Top row */}
        <motion.div
          className="absolute left-6 right-6 top-6 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.3em] text-bone/50 md:left-10 md:right-10 md:top-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <span>HashTag Marketing</span>
          <span className="hidden sm:block">Gurgaon, IN</span>
        </motion.div>

        {/* Center: glowing # draw */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.svg
            width="120"
            height="120"
            viewBox="0 0 100 100"
            fill="none"
            style={{ filter: "drop-shadow(0 0 24px rgba(15,169,156,0.45))" }}
            animate={complete && !exiting ? { scale: [1, 1.12, 1] } : { scale: 1 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            {STROKES.map((d, i) => (
              <g key={i}>
                <motion.path
                  d={d}
                  stroke="rgba(244,244,240,0.12)"
                  strokeWidth="7"
                  strokeLinecap="square"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.45, delay: 0.15 + i * 0.13, ease: [0.65, 0, 0.35, 1] }}
                />
                <motion.path
                  d={d}
                  stroke="#0FA99C"
                  strokeWidth="7"
                  strokeLinecap="square"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 1.15, delay: 0.3 + i * 0.14, ease: [0.65, 0, 0.35, 1] }}
                />
              </g>
            ))}
          </motion.svg>
        </div>

        {/* Word ticker */}
        <div className="absolute bottom-[26%] left-1/2 h-6 -translate-x-1/2 overflow-hidden sm:bottom-[24%]">
          <AnimatePresence mode="popLayout">
            <motion.span
              key={wordIdx}
              className="block text-center text-xs font-bold uppercase tracking-[0.35em] text-bone/70"
              initial={{ y: 22, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -22, opacity: 0 }}
              transition={{ duration: 0.32, ease: EASE }}
            >
              {WORDS[wordIdx]}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Giant counter */}
        <div className="absolute bottom-2 right-4 md:bottom-4 md:right-8">
          <span
            className={`font-display text-[22vw] font-black leading-none tabular-nums transition-colors duration-300 md:text-[13rem] ${
              complete ? "text-teal-500" : "stroke-bone"
            }`}
            style={complete ? { color: "#0FA99C", WebkitTextStroke: "0px" } : undefined}
          >
            {count}
          </span>
        </div>

        {/* Progress hairline */}
        <div className="absolute bottom-0 left-0 h-[3px] w-full bg-bone/10">
          <motion.div
            className="h-full bg-teal-500"
            style={{ width: `${count}%`, backgroundColor: "#0FA99C" }}
          />
        </div>

        {/* Bottom-left tagline */}
        <motion.p
          className="absolute bottom-6 left-6 max-w-[180px] text-[10px] font-bold uppercase leading-relaxed tracking-[0.3em] text-bone/50 md:bottom-10 md:left-10"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          Integrated Brand &amp; Communications
        </motion.p>
      </motion.div>
    </div>
  );
}
